<template>
  <div class="table-responsive" v-if="hasData" :style="`direction:${layoutDirection}`">
    <table class="table table-hover">
      <thead class="thead-light">
        <tr>
          <th scope="col" v-for="column in visibleColumns" :key="column.key">
            {{ Lang.get(column.label) }}
          </th>
        </tr>
      </thead>
      <transition-group name="table-rows" tag="tbody" mode="out-in">
        <!-- No results row -->
        <tr class="table-secondary" :key="0" v-if="!hasData">
          <td class="text-center" :colspan="visibleColumns.length">
            {{ Lang.get('messages.no_results') }}
          </td>
        </tr>
        
        <!-- Data rows -->
        <template v-if="tableType === 'hierarchical'">
          <!-- Hierarchical structure (like first table) -->
          <template v-for="(yearLevel, yearsIndex) in data">
            <template v-for="(classItem, classesIndex) in yearLevel.classes">
              <tr v-for="(item, index) in classItem.students" :key="`student-${yearsIndex}-${classesIndex}-${index}`">
                <td v-for="column in visibleColumns" :key="column.key" :class="column.cellClass || ''">
                  <template v-if="column.key === '#'">
                    {{ index + 1 }}
                  </template>
                  <template v-else>
                    {{ getNestedValue(item, column.path, column.fallback) }}
                  </template>
                </td>
              </tr>
            </template>
          </template>
          <!-- Total summary row -->
          <tr key="total-summary" v-if="totalCount > 0 && showSummaries">
            <td colspan="2"></td>
            <th>{{ Lang.get('messages.school_count') }}</th>
            <th>{{ totalCount }}</th>
            <td :colspan="visibleColumns.length - 4"></td>
          </tr>
        </template>
        
        <template v-else>
          <!-- Flat structure (like second table) -->
          <tr v-for="(item, index) in data" :key="`item-${index}`">
            <td v-for="column in visibleColumns" :key="column.key" :class="column.cellClass || ''">
              <template v-if="column.key === '#'">
                {{ index + 1 }}
              </template>
              <template v-else>
                {{ getNestedValue(item, column.path, column.fallback) }}
              </template>
            </td>
          </tr>
        </template>
      </transition-group>
    </table>
  </div>
</template>

<script>
import Lang from "../../lang";

export default {
    
  name: 'StudentDateReport',
  props: {
    // Data to display
    data: {
      type: Array,
      default: () => []
    },
    
    // Column configuration
    columns: {
      type: Array,
      required: true,
      // Expected format:
      // [
      //   { key: '#', label: 'messages.#', path: null },
      //   { key: 'student_code', label: 'messages.student_code', path: 'student_code' },
      //   { key: 'full_name', label: 'messages.Student_Name', path: 'full_name' },
      //   { key: 'stage', label: 'messages.stage', path: 'yearLevel.stage.name' },
      //   { 
      //     key: 'nationality', 
      //     label: 'messages.Nationality', 
      //     path: 'original_nationality.name',
      //     fallback: 'messages.unspecified_nationality',
      //     condition: () => true // Optional visibility condition
      //   }
      // ]
    },
    
    // Table type: 'flat' or 'hierarchical'
    tableType: {
      type: String,
      default: 'flat',
      validator: (value) => ['flat', 'hierarchical'].includes(value)
    },
    
    // Layout direction
    layoutDirection: {
      type: String,
      default: 'ltr'
    },
    
    // Show summary rows (for hierarchical tables)
    showSummaries: {
      type: Boolean,
      default: true
    },
    
    // Total count (for hierarchical tables)
    totalCount: {
      type: Number,
      default: 0
    },
    
    // Global modules access (for conditional columns)
    mods: {
      type: Object,
      default: () => ({})
    }
  },
  
  computed: {
    hasData() {
      return this.data && this.data.length > 0;
    },
    
    visibleColumns() {
      return this.columns.filter(column => {
        if (column.condition) {
          return column.condition(this.mods);
        }
        return true;
      });
    }
  },
  
  methods: {
    getNestedValue(obj, path, fallback = '') {
      if (!path) return '';
      
      // Use lodash get if available, otherwise implement simple nested access
      if (window._ && _.get) {
        const value = _.get(obj, path);
        return value !== undefined ? value : (fallback ? Lang.get(fallback) : '');
      }
      
      // Simple nested object access
      const keys = path.split('.');
      let result = obj;
      
      for (const key of keys) {
        if (result && typeof result === 'object' && key in result) {
          result = result[key];
        } else {
          return fallback ? Lang.get(fallback) : '';
        }
      }
      
      return result !== undefined ? result : (fallback ? Lang.get(fallback) : '');
    }
  }
};
</script>

<style scoped>
.table-responsive {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  background: white;
}

.table {
  margin-bottom: 0;
}

.table thead th {
  background-color: #f8fafc;
  color: #1d5166;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  border-top: none;
  border-bottom: 2px solid #edf2f7;
  padding: 16px;
}

.table tbody td {
  padding: 16px;
  vertical-align: middle;
  color: #4a5568;
  border-bottom: 1px solid #edf2f7;
}

.table-hover tbody tr:hover {
  background-color: rgba(29, 81, 102, 0.02);
}

.summary-row {
  background-color: #f1f5f9;
}

.summary-row th, .summary-row td {
  color: #1e293b;
  font-weight: 700;
}

/* Animations */
.table-rows-enter-active,
.table-rows-leave-active {
  transition: all 0.3s ease;
}

.table-rows-enter-from,
.table-rows-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>